/**
 * InformeLiquidacion — vista previa del documento que se le manda a la compañía.
 *
 * RUTA PROPIA Y NO UN PANEL DENTRO DEL DÍA. Es un papel para un tercero; meterlo en la
 * vista operativa mezclaría un documento con los botones que mueven plata.
 *
 * ESTA PANTALLA ES LA VISTA PREVIA. Sirve para revisar el informe antes de emitirlo; el
 * PDF lo genera Playwright en el servidor (`POST /api/pdf/generate`), que es lo que le da
 * márgenes fijos, «Página X de Y» y registro en `informes_accesos`. No se imprime desde el
 * navegador: dos formas de producir el mismo papel es una diferencia que aparece cuando la
 * aseguradora reclama.
 *
 * EL CICLO NO SE EJECUTA ACÁ. `GET /api/informe` no tiene POST; los pasos que mueven plata
 * viven en `/yoolpay/dia`.
 *
 * @see src/services/yoolpay/informeApi.js — los datos
 * @see src/services/yoolpay/informePdfApi.js — el PDF
 * @see src/components/payments/yoolpay/informe/InformeDiaDoc.jsx
 * @see src/pages/payments/yoolpay/YoolPayDia.jsx — la pantalla operativa
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, RefreshCw } from 'lucide-react';

import { getInforme } from '../../../services/yoolpay/informeApi';
import { Aviso, Seccion } from '../../../components/payments/yoolpay/cierre/piezas';
import { Boton, EmptyState, Select } from '../../../components/payments/yoolpay/cierre/ds';
import AccionesInforme from '../../../components/payments/yoolpay/informe/AccionesInforme';
import InformeDiaDoc from '../../../components/payments/yoolpay/informe/InformeDiaDoc';
import InformeMesDoc from '../../../components/payments/yoolpay/informe/InformeMesDoc';
import {
  fmtFechaLargaDoc,
  fmtPeriodoDoc,
} from '../../../components/payments/yoolpay/informe/documento';

const TODAS = '__todas__';

export default function InformeLiquidacion({ tipo = 'dia' }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const fecha = params.get('fecha') ?? '';
  const period = params.get('period') ?? '';
  const compania = params.get('compania') ?? '';

  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  // `data === null` con `error` es «la llamada falló», distinto de un período sin
  // movimiento. Un documento vacío por un error se lee como «no se recaudó nada», que es
  // lo peor que puede decir un papel que va a la aseguradora.
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const token = await getToken();
      setData(await getInforme(token, { tipo, fecha, period }));
    } catch (e) {
      setData(null);
      setError(e?.message ?? 'No se pudo cargar el informe');
    } finally {
      setCargando(false);
    }
  }, [getToken, tipo, fecha, period]);

  useEffect(() => { load(); }, [load]);

  const informes = useMemo(() => data?.informes ?? [], [data]);

  /**
   * El filtro por compañía se aplica ACÁ y no en la llamada: así cambiar de compañía no
   * pide de nuevo el mismo período, y volver a «Todas» no espera nada.
   */
  const visibles = useMemo(
    () => (compania ? informes.filter((i) => i.compania_id === compania) : informes),
    [informes, compania],
  );

  const opciones = useMemo(
    () => [
      { value: TODAS, label: `Todas las compañías (${informes.length})` },
      ...informes.map((i) => ({
        value: i.compania_id,
        label: i.compania_nombre ?? i.compania_id,
      })),
    ],
    [informes],
  );

  const elegirCompania = useCallback((e) => {
    const value = e?.target?.value ?? e;
    const siguiente = new URLSearchParams(params);
    if (!value || value === TODAS) siguiente.delete('compania');
    else siguiente.set('compania', value);
    setParams(siguiente, { replace: true });
  }, [params, setParams]);

  const volver = useCallback(
    () => navigate(tipo === 'dia' ? `/yoolpay/dia?fecha=${fecha}` : '/yoolpay/mes'),
    [navigate, tipo, fecha],
  );

  const titulo = tipo === 'dia'
    ? `Informe del ${fmtFechaLargaDoc(fecha)}`
    : `Informe de ${fmtPeriodoDoc(period)}`;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Seccion
          titulo={titulo}
          bajada={
            'El documento que se le manda a la compañía: una fila por transacción, con lo '
            + 'que se le gira. Sólo lectura — el ciclo se ejecuta en la pantalla del día.'
          }
          derecha={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ minWidth: 240 }}>
                <Select
                  options={opciones}
                  value={compania || TODAS}
                  onChange={elegirCompania}
                  aria-label="Compañía"
                />
              </div>
              <Boton variant="outline" onClick={load} disabled={cargando} aria-label="Refrescar">
                <RefreshCw className={`h-4 w-4 ${cargando ? 'animate-spin' : ''}`} />
              </Boton>
              <Boton variant="outline" onClick={volver}>
                <ChevronLeft className="h-4 w-4" />
                Volver
              </Boton>
            </div>
          }
        >
          <div style={{ padding: '18px 24px 22px' }}>
            {error ? (
              <Aviso tono="alta" titulo="No se pudo cargar el informe">
                {error} — el documento está vacío porque la llamada falló, no porque no
                hubiera movimiento.
              </Aviso>
            ) : null}

            {!error && cargando && !data ? (
              <p style={{ fontSize: 13, color: 'var(--yool-muted)', margin: 0 }}>
                Armando el informe…
              </p>
            ) : null}

            {!error && !cargando && informes.length === 0 ? (
              <EmptyState
                title="Sin movimiento"
                text={
                  'Ninguna compañía tuvo cobros ni cuotas en este período. No hay informe '
                  + 'que emitir.'
                }
              />
            ) : null}

            {!error && informes.length > 0 && visibles.length === 0 ? (
              <EmptyState
                title="Esa compañía no tuvo movimiento"
                text="Elige otra compañía, o «Todas» para ver las que sí lo tuvieron."
              />
            ) : null}
          </div>
      </Seccion>

      {/* Las acciones van POR COMPAÑÍA, encima de su documento: el PDF se dirige a UNA
          aseguradora, así que con «Todas» seleccionado una acción global tendría que
          elegir destinatario por su cuenta. */}
      {visibles.map((informe) => (
        <React.Fragment key={informe.compania_id}>
          <AccionesInforme informe={informe} tipo={tipo} fecha={fecha} period={period} />
          {tipo === 'dia'
            ? <InformeDiaDoc informe={informe} />
            : <InformeMesDoc informe={informe} />}
        </React.Fragment>
      ))}
    </div>
  );
}
